import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { studyService } from '../services/studyService';
import { deckService } from '../services/deckService';
import { cardService } from '../services/cardService';
import { StudySession, Card, Deck } from '../types';
import { t } from '../utils/translations';
import { calculateDurationInSeconds } from '../utils/timeUtils';
import { ttsService } from '../utils/textToSpeech';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';
import CardEditModal from '../components/CardEditModal';
import SessionSummary from '../components/SessionSummary';
import NoCardsModal from '../components/NoCardsModal';
import ImageLightbox from '../components/ImageLightbox';
import { 
  SpacedRepetitionSettings, 
  DEFAULT_SPACED_REPETITION_SETTINGS 
} from '../components/StudyConfigModal';
import { useTheme } from '../contexts/ThemeContext';
import { 
  ArrowLeftIcon,
  EyeIcon,
  CheckIcon,
  XMarkIcon,
  ClockIcon,
  AcademicCapIcon,
  BoltIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowRightIcon,
  ArrowPathIcon,
  SpeakerWaveIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';
import StudySettingsModal from '../components/StudySettingsModal';

const Study: React.FC = () => {
  const { deckId } = useParams<{ deckId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();
  
  // Check if this is a quick study session
  const isQuickStudy = location.state?.isQuickStudy || false;
  const quickStudyData = location.state;
  
  const [deck, setDeck] = useState<Deck | null>(null);
  const [session, setSession] = useState<StudySession | null>(null);
  const [cards, setCards] = useState<Card[]>([]);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isFlipping, setIsFlipping] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState<number>(Date.now());
  const [cardStartTime, setCardStartTime] = useState<number>(Date.now());
  
  // TTS states
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [currentlyPlayingText, setCurrentlyPlayingText] = useState<string>('');
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [autoPlayAudio, setAutoPlayAudio] = useState<boolean>(
    () => localStorage.getItem('study.autoPlayAudio') === 'true'
  );

  const handleAutoPlayAudioChange = (value: boolean) => {
    setAutoPlayAudio(value);
    localStorage.setItem('study.autoPlayAudio', String(value));
    if (!value && ttsService.isSpeaking()) {
      ttsService.stop();
      setIsSpeaking(false);
      setCurrentlyPlayingText('');
    }
  };
  
  // Lightbox states
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState('');
  
  // Modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingCard, setEditingCard] = useState<Card | null>(null);
  const [showNoCardsModal, setShowNoCardsModal] = useState(false);
  const [nextReviewInfo, setNextReviewInfo] = useState<{
    nextReviewDate: Date | null;
    cardsToReview: number;
  }>({ nextReviewDate: null, cardsToReview: 0 });

  // Session tracking states
  const [sessionStats, setSessionStats] = useState({
    cardsStudied: 0,
    correctAnswers: 0,
    totalTimeSpent: 0
  });
  const [showSummary, setShowSummary] = useState(false);
  const [completedSession, setCompletedSession] = useState<StudySession | null>(null);
  
  // Track total cards for simple mode (to show incremented count when cards are re-added)
  const [totalCardsInSession, setTotalCardsInSession] = useState(0);
  
  // Spaced repetition settings
  const [spacedRepetitionSettings, setSpacedRepetitionSettings] = useState<SpacedRepetitionSettings>(DEFAULT_SPACED_REPETITION_SETTINGS);
  
  // Reference to answer buttons for focusing
  const answerButtonsRef = useRef<HTMLDivElement>(null);

  // Load spaced repetition settings from deck when deck is loaded (for re-renders after deck changes)
  useEffect(() => {
    if (deck) {
      const deckSettings: SpacedRepetitionSettings = {
        enabled: deck.spacedRepetitionEnabled !== undefined ? deck.spacedRepetitionEnabled : DEFAULT_SPACED_REPETITION_SETTINGS.enabled,
        intervals: {
          again: (deck as any).spacedRepetitionIntervals?.again || DEFAULT_SPACED_REPETITION_SETTINGS.intervals.again,
          hard: (deck as any).spacedRepetitionIntervals?.hard || DEFAULT_SPACED_REPETITION_SETTINGS.intervals.hard,
          good: (deck as any).spacedRepetitionIntervals?.good || DEFAULT_SPACED_REPETITION_SETTINGS.intervals.good,
          easy: (deck as any).spacedRepetitionIntervals?.easy || DEFAULT_SPACED_REPETITION_SETTINGS.intervals.easy,
        },
        multipliers: {
          hard: (deck as any).spacedRepetitionMultipliers?.hard || DEFAULT_SPACED_REPETITION_SETTINGS.multipliers.hard,
          good: (deck as any).spacedRepetitionMultipliers?.good || DEFAULT_SPACED_REPETITION_SETTINGS.multipliers.good,
          easy: (deck as any).spacedRepetitionMultipliers?.easy || DEFAULT_SPACED_REPETITION_SETTINGS.multipliers.easy,
        }
      };
      setSpacedRepetitionSettings(deckSettings);
    }
  }, [deck]);

  useEffect(() => {
    if (isQuickStudy && quickStudyData) {
      initializeQuickStudySession();
    } else if (deckId) {
      initializeStudySession();
    } else {
      // No deckId and not a quick study session - redirect to decks
      navigate('/decks');
    }
  }, [deckId, isQuickStudy]);

  const initializeQuickStudySession = async () => {
    try {
      setLoading(true);
      
      // Use the session and cards data passed from the quick study modal
      const sessionData = { _id: quickStudyData.sessionId } as StudySession;
      setSession(sessionData);
      
      const studyCards = quickStudyData.initialCards || [];
      setCards(studyCards);
      setTotalCardsInSession(studyCards.length);
      
      if (studyCards.length === 0) {
        // Quick study always shows modal (it's independent of spaced repetition mode)
        setShowNoCardsModal(true);
        return;
      }
      
      setSessionStartTime(Date.now());
      setCardStartTime(Date.now());
      
    } catch (error) {
      console.error('Failed to initialize quick study session:', error);
      toast.error('Failed to start quick study session');
      navigate('/decks');
    } finally {
      setLoading(false);
    }
  };

  const initializeStudySession = async () => {
    try {
      setLoading(true);
      
      // Get deck info
      const deckData = await deckService.getDeck(deckId!);
      setDeck(deckData);
      
      // Load spaced repetition settings from deck
      const deckSettings: SpacedRepetitionSettings = {
        enabled: deckData.spacedRepetitionEnabled !== undefined ? deckData.spacedRepetitionEnabled : DEFAULT_SPACED_REPETITION_SETTINGS.enabled,
        intervals: {
          again: (deckData as any).spacedRepetitionIntervals?.again || DEFAULT_SPACED_REPETITION_SETTINGS.intervals.again,
          hard: (deckData as any).spacedRepetitionIntervals?.hard || DEFAULT_SPACED_REPETITION_SETTINGS.intervals.hard,
          good: (deckData as any).spacedRepetitionIntervals?.good || DEFAULT_SPACED_REPETITION_SETTINGS.intervals.good,
          easy: (deckData as any).spacedRepetitionIntervals?.easy || DEFAULT_SPACED_REPETITION_SETTINGS.intervals.easy,
        },
        multipliers: {
          hard: (deckData as any).spacedRepetitionMultipliers?.hard || DEFAULT_SPACED_REPETITION_SETTINGS.multipliers.hard,
          good: (deckData as any).spacedRepetitionMultipliers?.good || DEFAULT_SPACED_REPETITION_SETTINGS.multipliers.good,
          easy: (deckData as any).spacedRepetitionMultipliers?.easy || DEFAULT_SPACED_REPETITION_SETTINGS.multipliers.easy,
        }
      };
      setSpacedRepetitionSettings(deckSettings);
      
      // Get cardLimit from location state or from deck configuration
      let cardLimit = location.state?.cardLimit;
      
      // If no cardLimit provided in location state, use deck's studyCardLimit
      if (!cardLimit) {
        cardLimit = (deckData as any).studyCardLimit || 20;
        console.log('Using deck studyCardLimit:', cardLimit);
      }
      
      console.log('Final cardLimit to be used:', cardLimit);
      
      // Start study session with optional cardLimit
      const sessionData = await studyService.startSession(deckId!, cardLimit);
      setSession(sessionData);
      
      // Get study cards - if spaced repetition is disabled, ignore schedule and get all cards
      const ignoreSchedule = !deckSettings.enabled;
      const { cards: studyCards } = await studyService.getStudyCards(sessionData._id, cardLimit, ignoreSchedule);
      console.log('Cards loaded:', studyCards.length);
      setCards(studyCards);
      setTotalCardsInSession(studyCards.length);
      
      if (studyCards.length === 0) {
        // Only show modal if spaced repetition is enabled
        // In simple mode (no spaced repetition), just redirect to decks
        if (deckSettings.enabled) {
          // Get next review information
          try {
            const reviewInfo = await cardService.getNextReviewInfo(deckId!);
            setNextReviewInfo({
              nextReviewDate: reviewInfo.nextReviewDate ? new Date(reviewInfo.nextReviewDate) : null,
              cardsToReview: reviewInfo.cardsToReview
            });
          } catch (error) {
            console.error('Failed to get next review info:', error);
          }
          
          setShowNoCardsModal(true);
        } else {
          // Simple mode - just go back to decks
          toast.success('Não há cards disponíveis para estudo no momento.');
          navigate('/decks');
        }
        return;
      }
      
      setSessionStartTime(Date.now());
      setCardStartTime(Date.now());
      
    } catch (error) {
      console.error('Failed to initialize study session:', error);
      toast.error('Failed to start study session');
      navigate('/decks');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = async (quality: number, correct: boolean) => {
    if (!session || !cards[currentCardIndex] || isFlipping) return; // Prevent multiple clicks during animation
    
    const timeSpentSeconds = calculateDurationInSeconds(cardStartTime);
    const currentCard = cards[currentCardIndex];
    
    try {
      // Start flipping animation for transition to next card
      if (currentCardIndex < cards.length - 1) {
        setIsFlipping(true);
      }

      await studyService.submitAnswer(session._id, {
        cardId: currentCard._id,
        quality,
        timeSpent: timeSpentSeconds,
        correct,
      });

      // Update session stats for current card (but not for last card, as completeStudySession will handle it)
      if (currentCardIndex < cards.length - 1) {
        setSessionStats(prev => ({
          cardsStudied: prev.cardsStudied + 1,
          correctAnswers: prev.correctAnswers + (correct ? 1 : 0),
          totalTimeSpent: prev.totalTimeSpent + timeSpentSeconds
        }));
      }
      
      // Move to next card or complete session
      if (currentCardIndex < cards.length - 1) {
        // Use timeout to manage card transition animation
        setTimeout(() => {
          setCurrentCardIndex(prev => prev + 1);
          setShowAnswer(false);
          setCardStartTime(Date.now());
        }, 300); // Half of flip animation duration
        
        // Hide loading state after animation completes
        setTimeout(() => {
          setIsFlipping(false);
        }, 600); // Match the CSS animation duration
      } else {
        await completeStudySession(correct);
      }
      
    } catch (error) {
      console.error('Failed to submit answer:', error);
      toast.error('Failed to submit answer');
      // Reset flipping state on error
      setIsFlipping(false);
    }
  };

  // Handler for simple mode (no spaced repetition) - "Novamente" adds card back to session
  const handleSimpleAnswer = async (correct: boolean) => {
    if (!session || !cards[currentCardIndex] || isFlipping) return;
    
    const timeSpentSeconds = calculateDurationInSeconds(cardStartTime);
    const currentCard = cards[currentCardIndex];
    
    try {
      setIsFlipping(true);

      // Submit answer to backend (quality 1 for wrong, 5 for correct)
      await studyService.submitAnswer(session._id, {
        cardId: currentCard._id,
        quality: correct ? 5 : 1,
        timeSpent: timeSpentSeconds,
        correct,
      });

      if (!correct) {
        // Card was wrong - add it back to the end of the session
        const updatedCards = [...cards];
        const wrongCard = updatedCards[currentCardIndex];
        
        // Remove from current position and add to the end
        updatedCards.splice(currentCardIndex, 1);
        updatedCards.push(wrongCard);
        
        setCards(updatedCards);
        
        // Increment total cards count to show progress with re-added cards
        setTotalCardsInSession(prev => prev + 1);
        
        // Update stats (card studied but not correct)
        setSessionStats(prev => ({
          cardsStudied: prev.cardsStudied + 1,
          correctAnswers: prev.correctAnswers,
          totalTimeSpent: prev.totalTimeSpent + timeSpentSeconds
        }));
        
        // Stay at the same index (next card will slide in)
        setTimeout(() => {
          setShowAnswer(false);
          setCardStartTime(Date.now());
        }, 300);
        
        setTimeout(() => {
          setIsFlipping(false);
        }, 600);
      } else {
        // Card was correct - move to next or complete
        setSessionStats(prev => ({
          cardsStudied: prev.cardsStudied + 1,
          correctAnswers: prev.correctAnswers + 1,
          totalTimeSpent: prev.totalTimeSpent + timeSpentSeconds
        }));
        
        if (currentCardIndex < cards.length - 1) {
          setTimeout(() => {
            setCurrentCardIndex(prev => prev + 1);
            setShowAnswer(false);
            setCardStartTime(Date.now());
          }, 300);
          
          setTimeout(() => {
            setIsFlipping(false);
          }, 600);
        } else {
          // All cards answered correctly - complete session
          await completeStudySession(true);
        }
      }
      
    } catch (error) {
      console.error('Failed to submit answer:', error);
      toast.error('Failed to submit answer');
      setIsFlipping(false);
    }
  };

  const completeStudySession = async (lastAnswerCorrect?: boolean) => {
    if (!session) return;
    
    const totalDurationSeconds = calculateDurationInSeconds(sessionStartTime);
    
    // Update final stats if this is the last card
    if (lastAnswerCorrect !== undefined) {
      const timeSpentSeconds = calculateDurationInSeconds(cardStartTime);
      setSessionStats(prev => ({
        cardsStudied: prev.cardsStudied + 1,
        correctAnswers: prev.correctAnswers + (lastAnswerCorrect ? 1 : 0),
        totalTimeSpent: prev.totalTimeSpent + timeSpentSeconds
      }));
    }
    
    try {
      const completedSessionData = await studyService.completeSession(session._id, totalDurationSeconds);
      setCompletedSession(completedSessionData);
      setShowSummary(true);
      toast.success(t('study.completedSession'));
    } catch (error) {
      console.error('Failed to complete session:', error);
      toast.error('Failed to complete session');
    }
  };

  const handleShowAnswer = () => {
    if (isFlipping) return; // Prevent multiple clicks during animation
    
    setIsFlipping(true);
    
    // Start the flip animation after a brief delay
    setTimeout(() => {
      setShowAnswer(true);
    }, 150); // Half of the flip animation duration
    
    // Hide loading state after animation completes and scroll to buttons
    setTimeout(() => {
      setIsFlipping(false);
      // Scroll answer buttons into view after the flip animation completes
      if (answerButtonsRef.current) {
        answerButtonsRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest',
          inline: 'nearest'
        });
      }
    }, 600); // Match the CSS animation duration
  };

  const handleHideAnswer = () => {
    if (isFlipping) return; // Prevent multiple clicks during animation
    
    setIsFlipping(true);
    
    // Start the flip animation after a brief delay
    setTimeout(() => {
      setShowAnswer(false);
    }, 150); // Half of the flip animation duration
    
    // Hide loading state after animation completes
    setTimeout(() => {
      setIsFlipping(false);
    }, 600); // Match the CSS animation duration
  };

  const handleSkip = () => {
    handleAnswer(1, false); // Low quality, incorrect
  };

  const handlePlayAudio = async (text: string) => {
    if (!ttsService.getIsSupported()) {
      toast.error(t('tts.notSupported'));
      return;
    }

    try {
      // Stop any currently playing audio
      if (isSpeaking) {
        ttsService.stop();
        setIsSpeaking(false);
        setCurrentlyPlayingText('');
        return;
      }

      setIsSpeaking(true);
      setCurrentlyPlayingText(text);

      // Uses ElevenLabs with automatic language detection
      await ttsService.speak(text);

      setIsSpeaking(false);
      setCurrentlyPlayingText('');
    } catch (error) {
      console.error('TTS Error:', error);
      toast.error(t('tts.error'));
      setIsSpeaking(false);
      setCurrentlyPlayingText('');
    }
  };

  // Clean up TTS when component unmounts or card changes
  useEffect(() => {
    return () => {
      if (ttsService.isSpeaking()) {
        ttsService.stop();
        setIsSpeaking(false);
        setCurrentlyPlayingText('');
      }
    };
  }, [currentCardIndex]);

  // Auto-play the visible side of the card when enabled
  useEffect(() => {
    if (!autoPlayAudio || loading || showSummary || isFlipping) return;
    const card = cards[currentCardIndex];
    if (!card || !ttsService.getIsSupported()) return;
    const text = showAnswer ? card.back : card.front;
    if (!text) return;

    let cancelled = false;
    setIsSpeaking(true);
    setCurrentlyPlayingText(text);
    ttsService
      .speak(text)
      .catch((error) => console.error('TTS Error:', error))
      .finally(() => {
        if (!cancelled) {
          setIsSpeaking(false);
          setCurrentlyPlayingText('');
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPlayAudio, currentCardIndex, showAnswer, loading, showSummary, isFlipping, cards.length]);

  const handleSummaryContinue = () => {
    // Navigate back to appropriate page
    if (isQuickStudy) {
      navigate('/decks');
    } else {
      navigate('/decks');
    }
  };

  const handleEditCard = () => {
    const currentCard = cards[currentCardIndex];
    setEditingCard(currentCard);
    setIsEditModalOpen(true);
  };

  const handleNoCardsModalClose = () => {
    setShowNoCardsModal(false);
    navigate('/decks');
  };

  const handleRestartProgress = async () => {
    if (!deckId) {
      throw new Error('Deck ID not found');
    }
    
    try {
      const result = await deckService.resetDeckProgress(deckId);
      toast.success(`Progresso reiniciado! ${result.modifiedCount} cards foram resetados.`);
      
      // Redirect back to decks page to start fresh
      navigate('/decks');
    } catch (error) {
      console.error('Failed to restart progress:', error);
      toast.error('Falha ao reiniciar o progresso do deck. Tente novamente.');
      throw error; // Re-throw to let modal handle the error state
    }
  };

  const handleDisableSpacedRepetition = async () => {
    if (!deckId) {
      throw new Error('Deck ID not found');
    }

    try {
      await deckService.updateDeck(deckId, { spacedRepetitionEnabled: false } as any);
      await deckService.resetDeckProgress(deckId);
      setSpacedRepetitionSettings(prev => ({ ...prev, enabled: false }));
      toast.success('Repetição espaçada desativada. O registro atual foi removido.');
      navigate('/decks');
    } catch (error) {
      console.error('Failed to disable spaced repetition:', error);
      toast.error('Falha ao desativar a repetição espaçada. Tente novamente.');
      throw error;
    }
  };

  const handleModalSave = async (front: string, back: string, difficulty: number, tags: string[], frontImage?: string, backImage?: string) => {
    if (!editingCard) return;
    
    try {
      // Build the update object
      const updateData: any = {
        front,
        back,
        difficulty,
        tags,
      };
      
      // Only include backImage in the update
      updateData.backImage = backImage || null;
      
      const updatedCard = await cardService.updateCard(editingCard._id, updateData);
      
      // Update the card in the local state - use only the updatedCard to ensure removed images don't persist
      const updatedCards = [...cards];
      updatedCards[currentCardIndex] = updatedCard;
      setCards(updatedCards);
      
      setIsEditModalOpen(false);
      setEditingCard(null);
      toast.success('Card updated successfully!');
    } catch (error) {
      console.error('Failed to update card:', error);
      toast.error('Failed to update card');
      throw error; // Re-throw to let modal handle the error state
    }
  };

  const handleDeleteCard = async () => {
    if (!editingCard) return;
    
    try {
      // Delete the card from the backend
      await cardService.deleteCard(editingCard._id);
      
      // Remove the card from the local state
      const updatedCards = cards.filter((_, index) => index !== currentCardIndex);
      setCards(updatedCards);
      
      // Handle navigation after deletion
      if (updatedCards.length === 0) {
        // No more cards left - end the study session
        if (session) {
          try {
            await studyService.completeSession(session._id, calculateDurationInSeconds(sessionStartTime));
          } catch (error) {
            console.error('Failed to complete session:', error);
          }
        }
        
        // Close modal and navigate away
        setIsEditModalOpen(false);
        setEditingCard(null);
        
        // Navigate back to decks
        navigate('/decks');
      } else {
        // There are still cards left
        // If we deleted the last card, move to the previous one
        if (currentCardIndex >= updatedCards.length) {
          setCurrentCardIndex(updatedCards.length - 1);
        }
        // Otherwise stay at the same index (which now shows the next card)
        
        // Reset card state
        setShowAnswer(false);
        setCardStartTime(Date.now());
        
        // Close modal
        setIsEditModalOpen(false);
        setEditingCard(null);
      }
    } catch (error) {
      console.error('Failed to delete card:', error);
      throw error; // Re-throw to let modal handle the error state
    }
  };

  // Calculate next interval for each quality option
  const calculateNextInterval = (quality: number, currentCard: Card) => {
    const reviewCount = currentCard.reviewCount || 0;
    const easeFactor = currentCard.easeFactor || 2.5;
    const currentInterval = currentCard.interval || 1;
    
    // If spaced repetition is disabled, return fixed intervals
    if (!spacedRepetitionSettings.enabled) {
      return 1; // Always 1 day when disabled
    }

    // Get custom intervals from settings
    const { intervals, multipliers } = spacedRepetitionSettings;

    if (quality < 3) {
      return intervals.again; // "Novamente" - use configured interval
    }

    // Calculate NEW ease factor based on quality
    let newEaseFactor = easeFactor + (0.1 - (5 - quality) * (0.08 + (5 - quality) * 0.02));
    newEaseFactor = Math.max(1.3, newEaseFactor);

    // Calculate interval based on review count and quality
    const isRelearningAfterAgain = reviewCount === 1 && currentInterval === 1;
    if (reviewCount === 0 || isRelearningAfterAgain) {
      // First review: use configured initial intervals
      if (quality === 5) {
        return intervals.easy; // Fácil
      } else if (quality === 4) {
        return intervals.good; // Bom
      } else {
        return intervals.hard; // Difícil
      }
    } else if (reviewCount === 1) {
      // Second review: use configured intervals with small multiplier
      if (quality === 5) {
        return Math.round(intervals.easy * 1.5); // Fácil
      } else if (quality === 4) {
        return Math.round(intervals.good * 1.3); // Bom
      } else {
        return intervals.hard; // Difícil stays same
      }
    } else {
      // For subsequent reviews, calculate based on quality
      // Match backend logic to ensure consistency
      const baseInterval = Math.max(currentInterval, 1);
      
      // Calculate intervals ensuring they are always in ascending order
      // Difícil < Bom < Fácil
      const difficultInterval = baseInterval + 1;
      const goodInterval = Math.max(Math.round(baseInterval * 1.5), difficultInterval + 1);
      const easyInterval = Math.max(Math.round(baseInterval * newEaseFactor), goodInterval + 1);
      
      if (quality === 5) {
        return easyInterval; // Fácil
      } else if (quality === 4) {
        return goodInterval; // Bom
      } else {
        return difficultInterval; // Difícil
      }
    }
  };

  const formatInterval = (days: number) => {
    if (days < 30) {
      return `${days}d`;
    } else if (days < 365) {
      const months = (days / 30).toFixed(1);
      return `${months}m`;
    } else {
      const years = (days / 365).toFixed(1);
      return `${years}y`;
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  // Show summary when session is completed
  if (showSummary && completedSession) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300 flex items-center justify-center px-4 py-6 sm:px-6 lg:px-8">
        <SessionSummary
          session={completedSession}
          cardsStudied={sessionStats.cardsStudied}
          correctAnswers={sessionStats.correctAnswers}
          totalDuration={sessionStats.totalTimeSpent}
          onContinue={handleSummaryContinue}
          isQuickStudy={isQuickStudy}
          deckName={deck?.name}
        />
      </div>
    );
  }

  if ((!isQuickStudy && !deck) || !session) {
    return <LoadingSpinner />;
  }

  // Show modal when no cards are available (only in spaced repetition mode or quick study)
  if (cards.length === 0 && (spacedRepetitionSettings.enabled || isQuickStudy)) {
    return (
      <>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300 flex items-center justify-center px-4 py-6 sm:px-6 lg:px-8">
          <LoadingSpinner />
        </div>
        <NoCardsModal
          isOpen={showNoCardsModal}
          onClose={handleNoCardsModalClose}
          onRestartProgress={handleRestartProgress}
          onDisableSpacedRepetition={handleDisableSpacedRepetition}
          deckName={deck?.name}
          isQuickStudy={isQuickStudy}
          nextReviewDate={nextReviewInfo.nextReviewDate}
          cardsToReview={nextReviewInfo.cardsToReview}
          spacedRepetitionEnabled={spacedRepetitionSettings.enabled}
        />
      </>
    );
  }

  // If no cards and spaced repetition is disabled, this shouldn't render (redirect happens in initialization)
  // But add safety check to prevent errors
  if (cards.length === 0) {
    return <LoadingSpinner />;
  }

  const currentCard = cards[currentCardIndex];
  const progress = ((currentCardIndex + 1) / cards.length) * 100;
  
  // For simple mode (no spaced repetition), show total cards including re-added ones
  const displayTotalCards = !spacedRepetitionSettings.enabled ? totalCardsInSession : cards.length;

  // Calculate card maturity based on review count and interval
  const getCardMaturity = (card: Card) => {
    if (card.reviewCount === 0) {
      return {
        level: 'new',
        label: 'Novo',
        color: 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200',
        dotColor: 'bg-blue-500'
      };
    } else if (card.interval < 21) {
      return {
        level: 'learning',
        label: 'Aprendendo',
        color: 'bg-orange-100 dark:bg-orange-900/20 text-orange-800 dark:text-orange-200',
        dotColor: 'bg-orange-500'
      };
    } else if (card.interval >= 21) {
      return {
        level: 'mature',
        label: 'Maduro',
        color: 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200',
        dotColor: 'bg-green-500'
      };
    } else {
      return {
        level: 'review',
        label: 'Revisão',
        color: 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-200',
        dotColor: 'bg-purple-500'
      };
    }
  };

  return (
    <div className="max-w-5xl mx-auto min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300 px-4 pt-4 pb-4 md:p-6">
      {/* Header with Mobile-style Back Button */}
      <div className="mb-6">
        {/* Mobile-style top bar with back button */}
        <div className="flex items-center mb-4">
          <button
            onClick={() => navigate('/decks')}
            className="inline-flex items-center p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200 mr-3"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <div className="flex-1">
            {isQuickStudy ? (
              <div className="hidden md:flex items-center">
                <div className="w-8 h-8 bg-gradient-to-br from-success-500 to-success-600 rounded-lg flex items-center justify-center mr-3">
                  <BoltIcon className="h-4 w-4 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900 dark:text-white">{t('study.quickStudy')}</h1>
                </div>
              </div>
            ) : (
              <div className="hidden md:block">
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">{deck?.name}</h1>
              </div>
            )}
          </div>
          <button
            onClick={() => setShowSettingsModal(true)}
            aria-label="Configurações"
            className="inline-flex items-center p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
          >
            <Cog6ToothIcon className="h-6 w-6" />
          </button>
        </div>
        
        {/* Additional info for Quick Study */}
        {isQuickStudy && (
          <div className="mb-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Cards misturados dos seus decks
            </p>
            {currentCard.deckId && typeof currentCard.deckId === 'object' && (currentCard.deckId as any).name && (
              <p className="text-xs text-primary-600 dark:text-primary-400 mt-1">
                De: {(currentCard.deckId as any).name}
              </p>
            )}
          </div>
        )}
        
        {/* Progress Bar */}
        <div className="flex items-center gap-3 w-full">
          <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden shadow-inner">
            <div
              className="bg-gradient-to-r from-blue-500 to-blue-600 dark:from-blue-600 dark:to-blue-700 h-3 rounded-full transition-all duration-500 ease-out shadow-sm"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="text-sm font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
            {currentCardIndex + 1}/{displayTotalCards}
          </div>
        </div>
      </div>

      {/* Flashcard */}
      <div className="relative w-full mx-auto mb-6">
        {/* Card Container with flip animation */}
        <div className="relative w-full h-[32rem] md:h-[36rem] perspective-1000">
          <div className={`card-flip-container ${showAnswer ? 'flipped' : ''}`}>
            {/* Front of Card */}
            <div className="card-face card-front">
              <div 
                className={`w-full h-full bg-gradient-to-br from-blue-50 to-blue-100 dark:from-gray-800 dark:to-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 transition-all duration-300 group ${!showAnswer ? 'cursor-pointer hover:shadow-xl hover:scale-105' : ''}`}
                onClick={!showAnswer ? handleShowAnswer : undefined}
              >
                <div className="p-4 md:p-8 h-full flex flex-col justify-center">
                  {/* Skip and Audio buttons on left, Edit button on right */}
                  <div className="absolute top-4 left-4 flex gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSkip();
                      }}
                      disabled={isFlipping}
                      className={`inline-flex items-center justify-center w-10 h-10 border text-xs font-medium rounded-full transition-all duration-200 shadow-sm ${
                        isFlipping 
                          ? 'border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 cursor-not-allowed opacity-75' 
                          : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 hover:shadow-md'
                      }`}
                      title={t('study.skip')}
                    >
                      <ArrowRightIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const currentCard = cards[currentCardIndex];
                        if (currentCard) {
                          const textToPlay = showAnswer ? currentCard.back : currentCard.front;
                          handlePlayAudio(textToPlay);
                        }
                      }}
                      disabled={isFlipping}
                      className={`inline-flex items-center justify-center w-10 h-10 border text-xs font-medium rounded-full transition-all duration-200 shadow-sm ${
                        isFlipping 
                          ? 'border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 cursor-not-allowed opacity-75' 
                          : isSpeaking && currentlyPlayingText === (showAnswer ? cards[currentCardIndex]?.back : cards[currentCardIndex]?.front)
                            ? 'border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                            : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 hover:shadow-md'
                      }`}
                      title={isSpeaking ? t('tts.stopAudio') : t('tts.playAudio')}
                    >
                      <SpeakerWaveIcon className={`h-4 w-4 ${isSpeaking && currentlyPlayingText === (showAnswer ? cards[currentCardIndex]?.back : cards[currentCardIndex]?.front) ? 'animate-pulse' : ''}`} />
                    </button>
                  </div>
                  <div className="absolute top-4 right-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditCard();
                      }}
                      className="inline-flex items-center justify-center w-10 h-10 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded-full text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 shadow-sm hover:shadow-md"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Card content */}
                  <div className="flex-1 flex items-center justify-center p-4 overflow-hidden">
                    {isFlipping ? (
                      <div className="flex items-center justify-center">
                        <LoadingSpinner />
                      </div>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center px-4">
                        <div className={`overflow-y-auto max-h-full w-full text-center ${
                          currentCard.front.length <= 300 ? 'text-lg md:text-xl' :
                          currentCard.front.length <= 600 ? 'text-base md:text-lg' :
                          currentCard.front.length <= 1000 ? 'text-sm md:text-base' :
                          'text-xs md:text-sm'
                        } text-gray-900 dark:text-white leading-relaxed max-w-full break-words`}>
                          <div dangerouslySetInnerHTML={{ __html: currentCard.front }} />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Click message */}
                  {!showAnswer && (
                    <div className="absolute bottom-4 left-0 right-0 flex justify-center">
                      <div className="bg-black bg-opacity-10 dark:bg-white dark:bg-opacity-10 backdrop-blur-sm rounded-full px-4 py-2">
                        <p className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                          </svg>
                          {t('study.clickToFlipCard')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Back of Card */}
            <div className="card-face card-back">
              <div 
                className="w-full h-full bg-gradient-to-br from-green-50 to-green-100 dark:from-gray-800 dark:to-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 cursor-pointer hover:shadow-xl hover:scale-105 transition-all duration-300 group"
                onClick={(e) => {
                  // Only flip back if clicking on the card background, not on buttons
                  const target = e.target as HTMLElement;
                  if (e.target === e.currentTarget || target.closest('.card-content-area')) {
                    handleHideAnswer();
                  }
                }}
              >
                <div className="p-4 md:p-8 h-full flex flex-col card-content-area">
                  {/* Skip and Audio buttons on left, Edit button on right */}
                  <div className="absolute top-4 left-4 flex gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSkip();
                      }}
                      disabled={isFlipping}
                      className={`inline-flex items-center justify-center w-10 h-10 border text-xs font-medium rounded-full transition-all duration-200 shadow-sm ${
                        isFlipping 
                          ? 'border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 cursor-not-allowed opacity-75' 
                          : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 hover:shadow-md'
                      }`}
                      title={t('study.skip')}
                    >
                      <ArrowRightIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const currentCard = cards[currentCardIndex];
                        if (currentCard) {
                          const textToPlay = showAnswer ? currentCard.back : currentCard.front;
                          handlePlayAudio(textToPlay);
                        }
                      }}
                      disabled={isFlipping}
                      className={`inline-flex items-center justify-center w-10 h-10 border text-xs font-medium rounded-full transition-all duration-200 shadow-sm ${
                        isFlipping 
                          ? 'border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 cursor-not-allowed opacity-75' 
                          : isSpeaking && currentlyPlayingText === (showAnswer ? cards[currentCardIndex]?.back : cards[currentCardIndex]?.front)
                            ? 'border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                            : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 hover:shadow-md'
                      }`}
                      title={isSpeaking ? t('tts.stopAudio') : t('tts.playAudio')}
                    >
                      <SpeakerWaveIcon className={`h-4 w-4 ${isSpeaking && currentlyPlayingText === (showAnswer ? cards[currentCardIndex]?.back : cards[currentCardIndex]?.front) ? 'animate-pulse' : ''}`} />
                    </button>
                  </div>
                  <div className="absolute top-4 right-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditCard();
                      }}
                      className="inline-flex items-center justify-center w-10 h-10 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded-full text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 shadow-sm hover:shadow-md"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Card content */}
                  <div className="flex-1 flex items-center justify-center p-4 overflow-hidden">
                    {isFlipping ? (
                      <div className="flex items-center justify-center">
                        <LoadingSpinner />
                      </div>
                    ) : (
                      <div className={`w-full flex flex-col md:flex-row items-${currentCard.back.length <= 600 ? 'center' : 'start'} ${currentCard.backImage ? 'gap-4 md:gap-6 px-4' : 'justify-center'} h-full`}>
                        {currentCard.backImage && (
                          <div className="flex-shrink-0 w-full md:order-2 md:w-auto mb-4 md:mb-0" style={{ maxWidth: '300px' }}>
                            <img 
                              src={currentCard.backImage} 
                              alt="Card visual" 
                              className="w-full h-auto max-h-64 object-contain rounded-lg shadow-md cursor-pointer hover:opacity-90 transition-opacity mx-auto md:mx-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                setLightboxImage(currentCard.backImage!);
                                setLightboxOpen(true);
                              }}
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                              title="Clique para ver em tamanho real"
                            />
                          </div>
                        )}
                        <div className={`${currentCard.backImage ? 'flex-1 md:order-1' : 'max-w-4xl'} overflow-y-auto max-h-full pr-2 ${
                          currentCard.back.length <= 300 ? 'text-lg md:text-xl' :
                          currentCard.back.length <= 600 ? 'text-base md:text-lg' :
                          currentCard.back.length <= 1000 ? 'text-sm md:text-base' :
                          'text-xs md:text-sm'
                        } ${currentCard.back.length <= 600 ? 'flex items-center' : ''} ${!currentCard.backImage ? 'text-center' : 'text-center md:text-left'} text-gray-900 dark:text-white leading-relaxed max-w-full break-words`}>
                          <div dangerouslySetInnerHTML={{ __html: currentCard.back }} />
                        </div>
                      </div>
                    )}
                  </div>


                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Answer Quality Buttons */}
      {showAnswer && (
        <div className="mb-6" ref={answerButtonsRef}>
          {spacedRepetitionSettings.enabled ? (
            /* Spaced Repetition Mode - 4 buttons */
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-4xl mx-auto">
              <button
                onClick={() => handleAnswer(1, false)}
                disabled={isFlipping}
                className={`group relative overflow-hidden flex flex-col items-center justify-center px-5 py-4 border-0 text-sm font-semibold rounded-2xl text-white transition-all duration-300 ${
                  isFlipping 
                    ? 'bg-gradient-to-br from-red-400 to-red-500 dark:from-red-600 dark:to-red-700 cursor-not-allowed opacity-60' 
                    : 'bg-gradient-to-br from-red-500 to-red-600 dark:from-red-600 dark:to-red-700 hover:from-red-600 hover:to-red-700 dark:hover:from-red-500 dark:hover:to-red-600 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]'
                } focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800`}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="flex items-center gap-2 mb-1 relative z-10">
                  <XMarkIcon className="h-5 w-5" />
                  <span>Novamente</span>
                </div>
                <span className="text-xs font-medium opacity-90 relative z-10">{formatInterval(calculateNextInterval(1, currentCard))}</span>
              </button>
              <button
                onClick={() => handleAnswer(3, false)}
                disabled={isFlipping}
                className={`group relative overflow-hidden flex flex-col items-center justify-center px-5 py-4 border-0 text-sm font-semibold rounded-2xl text-white transition-all duration-300 ${
                  isFlipping 
                    ? 'bg-gradient-to-br from-amber-400 to-amber-500 dark:from-amber-600 dark:to-amber-700 cursor-not-allowed opacity-60' 
                    : 'bg-gradient-to-br from-amber-500 to-amber-600 dark:from-amber-600 dark:to-amber-700 hover:from-amber-600 hover:to-amber-700 dark:hover:from-amber-500 dark:hover:to-amber-600 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]'
                } focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800`}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="flex items-center gap-2 mb-1 relative z-10">
                  <ClockIcon className="h-5 w-5" />
                  <span>Difícil</span>
                </div>
                <span className="text-xs font-medium opacity-90 relative z-10">{formatInterval(calculateNextInterval(3, currentCard))}</span>
              </button>
              <button
                onClick={() => handleAnswer(4, true)}
                disabled={isFlipping}
                className={`group relative overflow-hidden flex flex-col items-center justify-center px-5 py-4 border-0 text-sm font-semibold rounded-2xl text-white transition-all duration-300 ${
                  isFlipping 
                    ? 'bg-gradient-to-br from-green-400 to-green-500 dark:from-green-600 dark:to-green-700 cursor-not-allowed opacity-60' 
                    : 'bg-gradient-to-br from-green-500 to-green-600 dark:from-green-600 dark:to-green-700 hover:from-green-600 hover:to-green-700 dark:hover:from-green-500 dark:hover:to-green-600 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]'
                } focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800`}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="flex items-center gap-2 mb-1 relative z-10">
                  <CheckIcon className="h-5 w-5" />
                  <span>Bom</span>
                </div>
                <span className="text-xs font-medium opacity-90 relative z-10">{formatInterval(calculateNextInterval(4, currentCard))}</span>
              </button>
              <button
                onClick={() => handleAnswer(5, true)}
                disabled={isFlipping}
                className={`group relative overflow-hidden flex flex-col items-center justify-center px-5 py-4 border-0 text-sm font-semibold rounded-2xl text-white transition-all duration-300 ${
                  isFlipping 
                    ? 'bg-gradient-to-br from-blue-400 to-blue-500 dark:from-blue-600 dark:to-blue-700 cursor-not-allowed opacity-60' 
                    : 'bg-gradient-to-br from-blue-500 to-blue-600 dark:from-blue-600 dark:to-blue-700 hover:from-blue-600 hover:to-blue-700 dark:hover:from-blue-500 dark:hover:to-blue-600 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]'
                } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800`}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="flex items-center gap-2 mb-1 relative z-10">
                  <AcademicCapIcon className="h-5 w-5" />
                  <span>Fácil</span>
                </div>
                <span className="text-xs font-medium opacity-90 relative z-10">{formatInterval(calculateNextInterval(5, currentCard))}</span>
              </button>
            </div>
          ) : (
            /* Simple Mode - 2 buttons only */
            <div className="grid grid-cols-2 gap-4 max-w-lg mx-auto">
              <button
                onClick={() => handleSimpleAnswer(false)}
                disabled={isFlipping}
                className={`group relative overflow-hidden flex items-center justify-center gap-3 px-8 py-5 border-0 text-base font-semibold rounded-2xl text-white transition-all duration-300 ${
                  isFlipping 
                    ? 'bg-gradient-to-br from-red-400 to-red-500 dark:from-red-600 dark:to-red-700 cursor-not-allowed opacity-60' 
                    : 'bg-gradient-to-br from-red-500 to-red-600 dark:from-red-600 dark:to-red-700 hover:from-red-600 hover:to-red-700 dark:hover:from-red-500 dark:hover:to-red-600 shadow-xl hover:shadow-2xl transform hover:scale-[1.02] active:scale-[0.98]'
                } focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800`}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <XMarkIcon className="h-6 w-6 relative z-10" />
                <span className="relative z-10">Novamente</span>
              </button>
              <button
                onClick={() => handleSimpleAnswer(true)}
                disabled={isFlipping}
                className={`group relative overflow-hidden flex items-center justify-center gap-3 px-8 py-5 border-0 text-base font-semibold rounded-2xl text-white transition-all duration-300 ${
                  isFlipping 
                    ? 'bg-gradient-to-br from-green-400 to-green-500 dark:from-green-600 dark:to-green-700 cursor-not-allowed opacity-60' 
                    : 'bg-gradient-to-br from-green-500 to-green-600 dark:from-green-600 dark:to-green-700 hover:from-green-600 hover:to-green-700 dark:hover:from-green-500 dark:hover:to-green-600 shadow-xl hover:shadow-2xl transform hover:scale-[1.02] active:scale-[0.98]'
                } focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800`}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <CheckIcon className="h-6 w-6 relative z-10" />
                <span className="relative z-10">Correto</span>
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* Card Edit Modal */}
      <CardEditModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingCard(null);
        }}
        card={editingCard}
        onSave={handleModalSave}
        onDelete={handleDeleteCard}
      />

      {/* No Cards Modal */}
      <NoCardsModal
        isOpen={showNoCardsModal}
        onClose={handleNoCardsModalClose}
        onRestartProgress={handleRestartProgress}
        onDisableSpacedRepetition={handleDisableSpacedRepetition}
        deckName={deck?.name}
        isQuickStudy={isQuickStudy}
        nextReviewDate={nextReviewInfo.nextReviewDate}
        cardsToReview={nextReviewInfo.cardsToReview}
        spacedRepetitionEnabled={spacedRepetitionSettings.enabled}
      />

      <StudySettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        autoPlayAudio={autoPlayAudio}
        onAutoPlayAudioChange={handleAutoPlayAudioChange}
      />

      {/* Image Lightbox */}
      <ImageLightbox
        isOpen={lightboxOpen}
        imageUrl={lightboxImage}
        onClose={() => setLightboxOpen(false)}
        alt="Card Image"
      />
    </div>
  );
};

export default Study;
